import { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectIsFileDetailModal } from '@/store/selectors/ui';
import {
  selectFileDetailPayload,
  selectFileDetailId,
  selectFiles,
  selectSixId,
} from '@/store/selectors/sixDetail';
import { hideModal } from '@/store/thunks/ui/uiThunk';
import { closeFileDetailModal } from '@/store/slices/sixDetail/sixDetailSlice';
import { toggleFileFavoriteThunk } from '@/store/thunks/sixDetail/toggleFileFavoriteThunk';
import { downloadAndDecryptFileThunk } from '@/store/thunks/poc/helia';
import { mapFileEntryToFileMetadata } from '@/store/mappers/mapSixDetail';
import { saveBlobToDiskThunk } from '@/store/thunks/files/saveBlobToDiskThunk';
import { showErrorToast } from '@/utils/toast.utils';
import { userHasFileKey } from '@/utils/fileAccess.utils';
import { MIN_LEN } from '@/constants';

interface UseFileDetailModalState {
  isOpen: boolean;
  fileDetailPayload: ReturnType<typeof selectFileDetailPayload>;
  fileDetailId: ReturnType<typeof selectFileDetailId>;
  isFavorited: boolean;
}

interface UseFileDetailModalHandlers {
  handleClose: () => void;
  handleOpenChange: (nextOpen: boolean) => void;
  handleToggleFavorite: () => void;
  handleDownload: () => void;
}

interface UseFileDetailModalReturn {
  state: UseFileDetailModalState;
  handlers: UseFileDetailModalHandlers;
}

export function useFileDetailModal(): UseFileDetailModalReturn {
  const dispatch = useAppDispatch();

  const isOpen = useAppSelector(selectIsFileDetailModal);
  const fileDetailPayload = useAppSelector(selectFileDetailPayload);
  const fileDetailId = useAppSelector(selectFileDetailId);
  const files = useAppSelector(selectFiles);
  const sixId = useAppSelector(selectSixId);
  const currentUserId = useAppSelector((state) => state.auth.userId ?? state.user.user?.id);

  const fileEntry = useMemo(
    () => files.find((f) => f.id === fileDetailId),
    [files, fileDetailId],
  );

  const isFavorited = fileEntry?.isFavorite === true;

  const handleClose = useCallback((): void => {
    dispatch(hideModal());
    dispatch(closeFileDetailModal());
  }, [dispatch]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean): void => {
      if (!nextOpen) {
        dispatch(hideModal());
        dispatch(closeFileDetailModal());
      }
    },
    [dispatch],
  );

  const handleToggleFavorite = useCallback((): void => {
    if (typeof sixId !== 'string' || sixId.trim().length < MIN_LEN) return;
    if (fileDetailId === undefined) return;
    void dispatch(toggleFileFavoriteThunk({ sixId, fileId: fileDetailId }));
  }, [dispatch, sixId, fileDetailId]);

  const handleDownload = useCallback((): void => {
    if (fileEntry?.kind !== 'file') return;
    if (typeof sixId !== 'string' || sixId.trim().length < MIN_LEN) return;
    if (typeof fileEntry.cid !== 'string' || fileEntry.cid.trim().length === 0) {
      showErrorToast('File is missing CID metadata');
      return;
    }
    if (!userHasFileKey(fileEntry, currentUserId)) {
      showErrorToast('You do not have access to decrypt this file');
      return;
    }

    const fileMetadata = mapFileEntryToFileMetadata(fileEntry, sixId);

    void (async() => {
      const resultAction = await dispatch(
        downloadAndDecryptFileThunk({ sixId, fileMetadata }),
      );

      if (downloadAndDecryptFileThunk.fulfilled.match(resultAction)) {
        void dispatch(saveBlobToDiskThunk({
          blob: resultAction.payload.decryptedFile,
          filename: resultAction.payload.originalFileName,
        }));
        return;
      }

      showErrorToast(
        resultAction.payload?.message ??
          resultAction.error.message ??
          'Failed to download file',
      );
    })();
  }, [dispatch, sixId, fileEntry, currentUserId]);

  return {
    state: {
      isOpen,
      fileDetailPayload,
      fileDetailId,
      isFavorited,
    },
    handlers: {
      handleClose,
      handleOpenChange,
      handleToggleFavorite,
      handleDownload,
    },
  };
}
